import { LEGEND_BIN_COUNT, LEGEND_COLOR_VARS, ZONE_MASK_COLOR_VAR } from './constants';
import type { ComputeRateLegendParams, RateLegendBin } from './types';

// Formats a numeric rate for legend labels. We trim trailing zeros and cap
// at 2 decimal places — enough resolution for ag prescriptions (gal/ac,
// seeds/ac) without scientific notation for large magnitudes.
const formatRate = (value: number): string => {
    if (!Number.isFinite(value)) {
        return '—';
    }

    return Number.parseFloat(value.toFixed(2)).toString();
};

// Build a legend from a rate-bounds object.
//   - If min === max → one "zone mask" chip labeled with the single value.
//   - Else → LEGEND_BIN_COUNT (3) bins dividing [min, max] into equal thirds.
// Bins are half-open at the lower edge and closed at the upper edge for the
// final bin, mirroring Mapbox's `step` expression semantics.
export const computeRateLegend = ({ bounds }: ComputeRateLegendParams): RateLegendBin[] => {
    const { max, min } = bounds;

    if (min === max) {
        return [
            {
                colorVar: ZONE_MASK_COLOR_VAR,
                isZoneMask: true,
                label: formatRate(min),
                max,
                min
            }
        ];
    }

    const binWidth = (max - min) / LEGEND_BIN_COUNT;
    const bins: RateLegendBin[] = [];

    for (let index = 0; index < LEGEND_BIN_COUNT; index += 1) {
        const binMin = min + binWidth * index;
        const binMax = index === LEGEND_BIN_COUNT - 1 ? max : min + binWidth * (index + 1);

        bins.push({
            colorVar: LEGEND_COLOR_VARS[index],
            isZoneMask: false,
            label: `${formatRate(binMin)}–${formatRate(binMax)}`,
            max: binMax,
            min: binMin
        });
    }

    return bins;
};

export type { ComputeRateLegendParams, RateLegendBin } from './types';
